import { Dispatch, useEffect, useRef, useState, useCallback } from 'react';
import { Card } from './card';
import { AutoComplete, Checkbox } from '@/shared/ui';
import { useAppSelector } from '@/shared/hooks/store';
import { selectPrescriptionsPharmacies } from '@/store/slices/onboarding/selectors';
import { IPharmacy } from '@/shared/types/interfaces/pharmacy';
import { FormLabel } from '../../formLabel';
import { scrollToElement } from '@/shared/helpers/scroll';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { loadPharmacies } from '@/shared/helpers/pharmacyQuery';

import HelpArrowRotatedIcon from '@/assets/icons/help-arrow-rotated.svg';
import { AdvancedAutoComplete } from '@/shared/ui/autocomplete/adavanced-autoComplete';

interface IProps {
    isEditMode?: boolean;
    selectedPharmacy: IPharmacy | null;
    setSelectedPharmacy: Dispatch<React.SetStateAction<IPharmacy | null>>;
    error?: boolean;
    zipCode: string;
    disabled?: boolean;
}

const AutoCompleteWrapper = ({
    withoutLabel = false,
    error,
    loadOptionList,
    onSelect
}: {
    withoutLabel?: boolean;
    error?: boolean;
    loadOptionList: (query: string, signal: AbortSignal) => Promise<IPharmacy[]>;
    onSelect(value: IPharmacy): void;
}) => (
    <AdvancedAutoComplete<IPharmacy>
        required
        scrollToTitle
        uniqueIdentifier="pharmacyFinder"
        labelKey="display"
        className="mb-[10px]"
        label={!withoutLabel ? 'Where do you currently fill this prescription?' : ''}
        subLabel={
            <span className="font-semibold">
                Search by <span className="underline">name, address, or store number</span>
            </span>
        }
        inputVariant="advanced"
        popoverTitle="Search for your pharmacy"
        popoverInputVariant="advanced"
        placeholder="ex. CVS, Orlando, 542 Fox st., 123-3..."
        loadOptionsList={loadOptionList}
        onSelect={onSelect}
        error={error}
        popoverHelper={
            <i className="absolute right-[26px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                <p className="mr-2 mt-2">
                    It's <b>3x faster</b> <br /> using voice!
                </p>
                <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
            </i>
        }
    />
);

export const Pharmacy = ({ isEditMode, selectedPharmacy, error, zipCode, setSelectedPharmacy, disabled }: IProps) => {
    const [showAnotherPharmacySelection, setShowAnotherPharmacySelection] = useState(false);
    const existingPharmacies = useAppSelector(selectPrescriptionsPharmacies);

    const [pharmaciesList, setPharmaciesList] = useState<IPharmacy[]>(existingPharmacies || []);
    const [anotherPharmacy, setAnotherPharmacy] = useState<IPharmacy | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const isFirstPharmacy = useRef<boolean>(!pharmaciesList.length);

    const localLoadPharmacies = useCallback((query: string, signal: AbortSignal) => loadPharmacies(query, pharmaciesList, zipCode, signal), [pharmaciesList, zipCode]);

    useEffect(() => {
        if (isMobile && selectedPharmacy && wrapperRef.current) {
            scrollToElement(wrapperRef.current!, 100, 0);
        }
    }, [wrapperRef.current, isMobile, selectedPharmacy]);

    const onSelectPharmacy = (pharmacy: IPharmacy, addToList = true) => {
        setShowAnotherPharmacySelection(false);
        if (addToList) setAnotherPharmacy(pharmacy);

        if (selectedPharmacy?.phone === pharmacy.phone) return setSelectedPharmacy(null);

        setSelectedPharmacy(pharmacy);
        if (addToList) setPharmaciesList(pharmaciesList.concat(pharmacy));
    };

    const onChooseAnotherLocation = () => {
        setSelectedPharmacy(null);
        setShowAnotherPharmacySelection(!showAnotherPharmacySelection);
    };

    const removeSelectedPharmacy = (pharmacy: IPharmacy) => {
        setSelectedPharmacy(null);
        setPharmaciesList(pharmaciesList.filter(item => item.phone !== pharmacy.phone));
        setAnotherPharmacy(null);
    };

    const onStartUpdatingPharmacy = (pharmacy: IPharmacy) => {
        if (anotherPharmacy?.phone !== pharmacy.phone && !isEditMode) return;

        return () => {
            removeSelectedPharmacy(pharmacy);
            setShowAnotherPharmacySelection(true);
        };
    };

    if (!selectedPharmacy && !pharmaciesList.length)
        return <AutoCompleteWrapper loadOptionList={localLoadPharmacies} onSelect={onSelectPharmacy} error={error} />;

    return (
        <div data-testid="prescription-medication-form-pharmacy" ref={wrapperRef}>
            <FormLabel required label="Where do you currently fill this prescription?" className="mb-[15px]" />
            <div className="mb-3 mt-3 flex flex-col gap-[10px] rounded-[15px] border-2 bg-mint-200 p-[15px]">
                {pharmaciesList?.map(pharmacy => (
                    <div key={pharmacy.phone} className="flex w-full gap-[20px]">
                        {!isFirstPharmacy.current && (
                            <Checkbox
                                className="prescriptions__pharmacy_form_checkbox"
                                checked={selectedPharmacy?.phone === pharmacy.phone}
                                onChange={() => onSelectPharmacy(pharmacy, false)}
                                error={error}
                                testId="prescription-medication-form-pharmacy-select"
                            />
                        )}
                        <Card
                            testId="prescription-medication-form-pharmacy-card"
                            name={pharmacy.name}
                            addressLine1={pharmacy.addressLine1}
                            addressLine2={pharmacy.addressLine2}
                            onUpdate={onStartUpdatingPharmacy(pharmacy)}
                            disabled={disabled}
                        />
                    </div>
                ))}
                {!isEditMode && !isFirstPharmacy.current && !anotherPharmacy && (
                    <div className="flex w-full gap-[20px]">
                        <Checkbox
                            checked={showAnotherPharmacySelection}
                            className="prescriptions__pharmacy_form_checkbox gap-[20px] lg:gap-[20px]"
                            onChange={onChooseAnotherLocation}
                            error={error}
                            label="I fill this prescription at a different location"
                            testId="prescription-medication-form-pharmacy-different-location"
                        />
                    </div>
                )}
                {showAnotherPharmacySelection && (
                    <AutoCompleteWrapper withoutLabel loadOptionList={localLoadPharmacies} onSelect={onSelectPharmacy} />
                )}
            </div>
        </div>
    );
};
